import React from 'react';
import styled from 'styled-components';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { Portal } from '@material-ui/core';
import { CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { Dialog as EEComponentsDialog, DialogBody, palette, Typography } from '@dev/ee-components';
import { map } from 'lodash-es';

import { ApplicationState } from '@/store';
import { CloseDeleteCampaignModal, DeleteCampaignsStart } from '@/store/campaigns/actions';
import {
    getActiveCampaignNames,
    getAutomationNamesUsingPickedCampaigns,
    getUsedInAutomationsCampaignNames,
} from '@/services/tools/campaigns';

const StyledDialog = styled.div`
    word-break: break-word;
    z-index: 1500;

    & .MuiDialog-root {
        z-index: 1301 !important;
    }

    & .MuiDialogTitle-root {
        & > .MuiTypography-root {
            padding-right: 10%;
            overflow: hidden;
            & > div {
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
`;

const UnableCampaignDeleteNamesListItem = styled.div`
    padding: 20px 0;
    border-bottom: 1px solid ${palette.gray[2]};
    word-break: break-all;

    &:last-of-type {
        border-bottom: none;
    }
`;

const renderUnableCampaignDeleteNamesList = (names: string[], title: string): JSX.Element => (
    <>
        <Box mt={'24px'} />
        <Typography variant={'h3'}>{title}</Typography>
        <div>
            {map(
                names,
                (name: string, index: number): JSX.Element => (
                    <UnableCampaignDeleteNamesListItem key={`${name}-${index}`}>
                        {name}
                    </UnableCampaignDeleteNamesListItem>
                )
            )}
        </div>
    </>
);

const dialogBody = (
    campaignNames: string[],
    activeCampaignNames: string[],
    usedInAutomationsCampaignNames: string[],
    type: CampaignTypeFilter,
    t: TFunction<'translation'>
): JSX.Element[] | string => {
    const isDeletePossible = !(activeCampaignNames.length || usedInAutomationsCampaignNames.length);
    const deleteConfirmationText = type?.includes(CampaignTypeFilter.Draft)
        ? t('deletedraftcampaigns')
        : campaignNames.length > 1
        ? t('deletecampaignsconfirmation')
        : t('deletecampaignsandstatistics', { campaignname: campaignNames[0] });

    return isDeletePossible
        ? deleteConfirmationText
        : map(
              [
                  ...(activeCampaignNames.length
                      ? [
                            {
                                bodyText: t('deletecampaigninfoaboutsendingstatus'),
                                namesListTitle: t('campaigns'),
                                names: activeCampaignNames,
                            },
                        ]
                      : []),
                  ...(usedInAutomationsCampaignNames.length
                      ? [
                            {
                                bodyText: t('deletecampaigninfoaboutuseinginautomation'),
                                namesListTitle: t('automations'),
                                names: getAutomationNamesUsingPickedCampaigns(campaignNames),
                            },
                        ]
                      : []),
              ],
              ({ bodyText, names, namesListTitle }, index: number): JSX.Element => (
                  <React.Fragment key={`${namesListTitle}-${index}`}>
                      {index > 0 ? <Box mt={'35px'} /> : ''}
                      <DialogBody variant={'alert'}>{bodyText}</DialogBody>
                      {renderUnableCampaignDeleteNamesList(names, namesListTitle)}
                  </React.Fragment>
              )
          );
};

const CampaignDeleteDialog = (): JSX.Element => {
    const {
        deleteDialog: { isOpened, campaignNames, type },
        collection,
    } = useSelector((state: ApplicationState) => state.campaigns);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const activeCampaignNames = getActiveCampaignNames(campaignNames, collection);
    const usedInAutomationsCampaignNames = getUsedInAutomationsCampaignNames(campaignNames, collection);
    const isDeletePossible = !(activeCampaignNames.length || usedInAutomationsCampaignNames.length);
    const title = isDeletePossible
        ? t('deletecampaignstitle', { campaignslength: campaignNames.length })
        : t('cannotdeletecampaigns');
    const cancelLabel = isDeletePossible ? t('cancel') : t('close');

    const body = dialogBody(campaignNames, activeCampaignNames, usedInAutomationsCampaignNames, type, t);

    return (
        <Portal>
            <StyledDialog>
                <EEComponentsDialog
                    open={isOpened}
                    title={title}
                    body={body}
                    onDestructiveClick={
                        isDeletePossible
                            ? () => {
                                  dispatch(DeleteCampaignsStart(campaignNames, type));
                                  dispatch(CloseDeleteCampaignModal());
                              }
                            : null
                    }
                    onCancelClick={() => {
                        dispatch(CloseDeleteCampaignModal());
                    }}
                    cancelLabel={cancelLabel}
                    destructiveLabel={isDeletePossible ? t('delete') : null}
                />
            </StyledDialog>
        </Portal>
    );
};

export default CampaignDeleteDialog;
